// OGP リンクカードの画像を、同一オリジンの API（GET /api/og/image）経由で取得する。
//
// 相手サイトの絶対 URL を <img src> や crossOrigin 付きの Image で直接読むと、相手サイトが
// Cross-Origin-Resource-Policy を返す場合は遮断され（ERR_BLOCKED_BY_RESPONSE.NotSameOrigin）、
// Access-Control-Allow-Origin を返さない場合は CORS で失敗する。サーバ経由で取得して同一オリジンの
// object URL にすれば、どちらの制約も受けない。
//
// **キャッシュに載せるのは Blob であり、object URL ではない。** object URL を共有キャッシュに載せて
// 上限超過で解放すると、まだ表示中・読み込み中の利用者の URL を失効させてしまう。Blob をキャッシュし、
// object URL は利用者ごとに発行して利用者自身が解放する（release）ことで、解放の責任を 1 か所に閉じる。
// キャッシュから溢れた Blob は参照が無くなった時点で GC が回収するため、解放漏れも起きない。
//
// 取得関数（fetchBlob）は呼び出し側から渡し、このモジュールは認証情報を持たない。

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCanvasApi } from "./canvas-api";
import { isUnpublishedObjectUrl } from "./canvas-raster-images";

/** Blob キャッシュの上限（件）。アバター（PORTABLE_AVATAR_CACHE_MAX_ENTRIES）と同じ。 */
export const OG_IMAGE_CACHE_MAX_ENTRIES = 256;

/**
 * Blob キャッシュの総バイト上限（64 MB）。
 * アバターのキャッシュ値は数 KB の圧縮済み data URL だが、OGP 画像はサーバ側の上限
 * （`OG_IMAGE_MAX_BYTES` = 3 MB）まであり得る。件数だけで縛ると最悪 256 × 3 MB ≒ 768 MB を
 * タブが抱えてしまうため、総バイト数でも縛る。典型的な OGP 画像（数十〜数百 KB）なら
 * 件数上限の 256 件が先に効き、この上限は大きい画像が多い場合の歯止めとして働く。
 */
export const OG_IMAGE_CACHE_MAX_BYTES = 64 * 1024 * 1024;

/** 元の画像 URL → 取得済み Blob。Map の挿入順を LRU として使う。 */
const blobByImageUrl = new Map<string, Blob>();
/** blobByImageUrl が保持している Blob の合計バイト数。 */
let cachedBytes = 0;
/** 同じ URL への同時要求を 1 本へまとめるための in-flight 表。 */
const inFlightLoads = new Map<string, Promise<Blob | undefined>>();
/** 再取得しても結果が変わらないと分かった URL（サーバが 400 / 502 を返したもの）。 */
const permanentFailures = new Set<string>();
/** 既に console.warn を出した URL（描画のたびに同じ警告を出さないために持つ）。 */
const warnedImageUrls = new Set<string>();

/** 画像 URL を Blob として取得する関数。認証は呼び出し側（canvas-api）が担う。 */
export type OgImageBlobFetcher = (imageUrl: string) => Promise<Blob>;

/**
 * リンクカード画像の読み込み元。
 * `src` を使い終わったら `release` を呼ぶ（object URL を解放する）。
 * `data:` のように後始末が要らない場合、`release` は省略される。
 */
export interface OgImageSource {
  readonly src: string;
  readonly release?: (() => void) | undefined;
}

/** リンクカードの imageUrl を、読み込み可能な URL へ解決する関数（サムネイル / PDF 用）。 */
export type OgImageResolver = (imageUrl: string) => Promise<OgImageSource | undefined>;

/** 取得を待たずに分かる状態。 */
type OgImageAvailability = "none" | "direct" | "unsupported" | "fetchable";

export interface OgImageObjectUrlState {
  /** <img src> に渡せる URL。取得中・取得失敗のときは undefined。 */
  readonly src: string | undefined;
  /** 取得できないことが確定したか（プレースホルダーを出す側の判定に使う）。 */
  readonly failed: boolean;
  /**
   * `<img>` の描画に失敗したときに呼ぶ。発行済みの object URL をその場で解放し、失敗として扱う。
   * 何度呼んでも二重解放にならない。
   */
  readonly reportRenderError: () => void;
}

/**
 * 画像 URL の扱いをスキームで決める。
 * - `http:` / `https:`: サーバ経由（プロキシ）で取得する。
 * - `data:`: 自己完結しているためそのまま使う。
 * - `blob:`: このブラウザ内でだけ解決できるローカルプレビュー（クリップボード貼り付けや attachment.update
 *   の途中経過）。サーバ中継へ回しても取得できないため、従来どおりそのまま描く。外部配信する
 *   サムネイル・自動保存スナップショットでは、これより手前の `excludeUnpublishedSrc=true`
 *   （canvas-raster-images.ts）が `blob:` を除外するため、未公開の画像が外部へ出ることはない。
 * - それ以外（解析できない文字列・`javascript:` など）: 読み込まない。
 */
function classifyImageUrl(rawImageUrl: string | undefined): OgImageAvailability {
  if (rawImageUrl === undefined || rawImageUrl.length === 0) return "none";
  // object URL かどうかは、外部配信の除外判定（canvas-raster-images.ts の skipUnpublished）と
  // 同じ関数で決める。ここだけ独自にスキームを判定すると、`BLOB:` のような表記で
  // 「除外はすり抜けるが描画はされる」食い違いが生まれる。
  if (isUnpublishedObjectUrl(rawImageUrl)) return "direct";
  let parsed: URL;
  try {
    parsed = new URL(rawImageUrl);
  } catch {
    return "unsupported";
  }
  if (parsed.protocol === "data:") return "direct";
  if (parsed.protocol === "http:" || parsed.protocol === "https:") return "fetchable";
  return "unsupported";
}

function readCachedBlob(imageUrl: string): Blob | undefined {
  const cached = blobByImageUrl.get(imageUrl);
  if (cached === undefined) return undefined;
  // Map の挿入順を LRU として使うため、参照された entry を末尾へ移動する。
  blobByImageUrl.delete(imageUrl);
  blobByImageUrl.set(imageUrl, cached);
  return cached;
}

function rememberBlob(imageUrl: string, blob: Blob): void {
  // 単体で総バイト上限を超える Blob はキャッシュしない（他をすべて捨てても収まらないため）。
  // 取得結果そのものは呼び出し元へ返るので表示はでき、次の要求では取り直しになる。
  if (blob.size > OG_IMAGE_CACHE_MAX_BYTES) return;
  const previous = blobByImageUrl.get(imageUrl);
  if (previous !== undefined) cachedBytes -= previous.size;
  blobByImageUrl.set(imageUrl, blob);
  cachedBytes += blob.size;
  while (
    blobByImageUrl.size > OG_IMAGE_CACHE_MAX_ENTRIES ||
    cachedBytes > OG_IMAGE_CACHE_MAX_BYTES
  ) {
    const oldestKey = blobByImageUrl.keys().next().value as string | undefined;
    if (oldestKey === undefined) return;
    // 参照を落とすだけでよい。利用者が発行済みの object URL は利用者が解放するため、
    // ここで失効させて「表示中の画像が消える」ことが起きない。溢れた Blob は GC が回収する。
    const oldest = blobByImageUrl.get(oldestKey);
    blobByImageUrl.delete(oldestKey);
    if (oldest !== undefined) cachedBytes -= oldest.size;
  }
}

/** 集合の件数を上限内に保つ（最も古い要素から捨てる。Set は挿入順を保つ）。 */
function rememberBounded(set: Set<string>, value: string): void {
  set.add(value);
  while (set.size > OG_IMAGE_CACHE_MAX_ENTRIES) {
    const oldest = set.values().next().value as string | undefined;
    if (oldest === undefined) return;
    set.delete(oldest);
  }
}

/**
 * 取得の失敗が恒久的か（再取得しても同じ結果か）を判定する。
 *
 * 記録するのはサーバが 400（取得してはいけない URL）と 502（相手サイトが画像を返さない・
 * 大きすぎる等）を返した場合だけである。認証の失効（401 / 403）とネットワークエラーは回復し得るため
 * 記録しない。記録してしまうと、再ログイン後もカードの画像が出ないままになる。
 *
 * fetchBlob は呼び出し側から渡すため、ここでは投げられたエラーの数値 `status` だけを構造的に見る
 * （canvas-api の CanvasApiHttpError がこの形を満たす）。
 */
function isPermanentFailure(error: unknown): boolean {
  const status = (error as { readonly status?: unknown } | null | undefined)?.status;
  return status === 400 || status === 502;
}

function warnOnce(imageUrl: string, error: unknown): void {
  if (warnedImageUrls.has(imageUrl)) return;
  rememberBounded(warnedImageUrls, imageUrl);
  console.warn("[og-image] リンクカードの画像を取得できませんでした", error);
}

/**
 * 取得できた Blob を「画像として使ってよいか」で判定する。
 *
 * サーバの中継（api の og-image.ts）は、相手サイトの Content-Type を検証してから画像だけを返す。
 * それでも画像でない Blob が届くのは、**HTTP 上は成功に見える別の応答が返っている**ときである。
 * 実際に起こるのは、オリジンが返した 403 / 404 を CloudFront がディストリビューション全体の
 * カスタムエラー応答で `/index.html`（200）へ置き換える経路で、`callBlob` は `response.ok` しか
 * 見ないため、この HTML が「成功した Blob」として流れてくる。
 *
 * これをキャッシュへ載せると、原因（束縛キャンバスの判定が古い、結果整合の遅れなど）が解消しても
 * 同じ URL を取り直さず、タブを開いているあいだプレースホルダーのままになる。失敗として扱い、
 * キャッシュへ載せない。`status` を持たないエラーなので `isPermanentFailure` は false になり、
 * 恒久失敗としても記録されず、次の再表示で取り直される。
 *
 * 見るのは `image/` で始まるかだけでよい。中継する media type の限定（`image/svg+xml` の除外、
 * 重複ヘッダーの排除）はサーバが行っており、ここは取得経路の一貫性のための検査である。
 */
function isUsableImageBlob(blob: Blob): boolean {
  return blob.type.toLowerCase().startsWith("image/");
}

/**
 * OGP リンクカードの画像を Blob として取得する。
 * 同じ URL への同時要求は 1 本へまとめ、結果はモジュールキャッシュへ載せる。
 * 取得できなかった場合は undefined を返し、恒久的な失敗は記録して再試行を繰り返さない。
 */
export async function loadOgImageBlob(
  rawImageUrl: string,
  fetchBlob: OgImageBlobFetcher
): Promise<Blob | undefined> {
  if (classifyImageUrl(rawImageUrl) !== "fetchable") return undefined;
  const cached = readCachedBlob(rawImageUrl);
  if (cached !== undefined) return cached;
  if (permanentFailures.has(rawImageUrl)) return undefined;

  const existing = inFlightLoads.get(rawImageUrl);
  if (existing !== undefined) return existing;

  const load = (async () => {
    try {
      const blob = await fetchBlob(rawImageUrl);
      if (!isUsableImageBlob(blob)) {
        throw new Error(`[og-image] 画像ではない応答を受け取りました（${blob.type || "型なし"}）`);
      }
      rememberBlob(rawImageUrl, blob);
      return blob;
    } catch (error) {
      if (isPermanentFailure(error)) {
        rememberBounded(permanentFailures, rawImageUrl);
      }
      warnOnce(rawImageUrl, error);
      return undefined;
    }
  })().finally(() => {
    if (inFlightLoads.get(rawImageUrl) === load) {
      inFlightLoads.delete(rawImageUrl);
    }
  });

  inFlightLoads.set(rawImageUrl, load);
  return load;
}

/**
 * リンクカードの imageUrl を、実際に読み込める URL へ解決する。
 * `http(s)` は取得した Blob から**呼び出しごとに新しい object URL** を発行し、`release` で解放させる。
 * `data:` と `blob:` はそのまま返す（`release` 無し。こちらが発行した object URL ではないため解放しない）。
 * それ以外のスキームは undefined を返す。
 */
export async function resolveOgImageSource(
  rawImageUrl: string,
  fetchBlob: OgImageBlobFetcher
): Promise<OgImageSource | undefined> {
  const availability = classifyImageUrl(rawImageUrl);
  if (availability === "direct") return { src: rawImageUrl };
  if (availability !== "fetchable") return undefined;

  const blob = await loadOgImageBlob(rawImageUrl, fetchBlob);
  if (blob === undefined) return undefined;
  const src = URL.createObjectURL(blob);
  return { src, release: () => URL.revokeObjectURL(src) };
}

interface OgImageResolvedState {
  /** この状態がどの画像 URL のものか。URL が変わった直後に前の結果を見せないために持つ。 */
  readonly key: string | undefined;
  readonly status: "none" | "pending" | "ready" | "failed";
  readonly src: string | undefined;
}

/** 取得を待たずに決まる初期状態（`data:` と読み込まないスキームはここで確定する）。 */
function initialResolvedState(rawImageUrl: string | undefined): OgImageResolvedState {
  const availability = classifyImageUrl(rawImageUrl);
  if (availability === "none") return { key: rawImageUrl, status: "none", src: undefined };
  if (availability === "direct") return { key: rawImageUrl, status: "ready", src: rawImageUrl };
  if (availability === "unsupported") return { key: rawImageUrl, status: "failed", src: undefined };
  return { key: rawImageUrl, status: "pending", src: undefined };
}

function isSameResolvedState(a: OgImageResolvedState, b: OgImageResolvedState): boolean {
  return a.key === b.key && a.status === b.status && a.src === b.src;
}

/**
 * React 用。内部で `useCanvasApi()` から取得関数を得るため、呼び出し側は画像 URL だけを渡す。
 * 取得中は `src` も `failed` も偽で、完了すると再レンダーされる。
 * 発行した object URL は、アンマウントと画像 URL の切り替えで必ず解放する。
 */
export function useOgImageObjectUrl(rawImageUrl: string | undefined): OgImageObjectUrlState {
  const { fetchOgpImage } = useCanvasApi();
  const [resolved, setResolved] = useState<OgImageResolvedState>(() =>
    initialResolvedState(rawImageUrl)
  );
  // 発行済みの object URL。解放の重複を避けるため、解放したら undefined へ戻す。
  const acquiredRef = useRef<OgImageSource | undefined>(undefined);
  // state が前の URL のものなら、その場で新しい URL の初期状態へ読み替える
  // （別カードの画像が一瞬出るのを防ぐ）。
  const current = resolved.key === rawImageUrl ? resolved : initialResolvedState(rawImageUrl);

  useEffect(() => {
    const immediate = initialResolvedState(rawImageUrl);
    // 同じ状態なら再レンダーを起こさない（描画のたびに 2 回描くのを避ける）。
    setResolved((previous) => (isSameResolvedState(previous, immediate) ? previous : immediate));
    if (immediate.status !== "pending" || rawImageUrl === undefined) return;

    let cancelled = false;
    void resolveOgImageSource(rawImageUrl, fetchOgpImage)
      .catch(() => undefined)
      .then((source) => {
        if (cancelled) {
          // 解決を待つ間にアンマウント / URL 切り替えが起きた場合は、その場で解放する。
          source?.release?.();
          return;
        }
        acquiredRef.current = source;
        setResolved({
          key: rawImageUrl,
          status: source === undefined ? "failed" : "ready",
          src: source?.src,
        });
      });
    return () => {
      cancelled = true;
      acquiredRef.current?.release?.();
      acquiredRef.current = undefined;
    };
  }, [fetchOgpImage, rawImageUrl]);

  const reportRenderError = useCallback(() => {
    // 描画に失敗した object URL は以後使わないため、アンマウントを待たずにここで解放する。
    acquiredRef.current?.release?.();
    acquiredRef.current = undefined;
    setResolved((previous) =>
      previous.status === "ready" && previous.src !== undefined
        ? { key: previous.key, status: "failed", src: undefined }
        : previous
    );
  }, []);

  return {
    src: current.status === "ready" ? current.src : undefined,
    failed: current.status === "failed",
    reportRenderError,
  };
}

/**
 * React の外（サムネイル / PDF のラスタ描画）から使う解決関数を作る。
 * 取得関数を束ねるだけで、キャッシュと同時取得の集約は loadOgImageBlob が担う。
 */
export function useOgImageResolver(): OgImageResolver {
  const { fetchOgpImage } = useCanvasApi();
  return useMemo<OgImageResolver>(
    () => (imageUrl) => resolveOgImageSource(imageUrl, fetchOgpImage),
    [fetchOgpImage]
  );
}

/** テスト用。モジュールキャッシュを初期状態へ戻す。 */
export function clearOgImageSourceCacheForTest(): void {
  blobByImageUrl.clear();
  cachedBytes = 0;
  inFlightLoads.clear();
  permanentFailures.clear();
  warnedImageUrls.clear();
}
